import React from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCRM } from '../../contexts/CRMContext';
import {
    LockClosedIcon,
    SparklesIcon,
    ExclamationTriangleIcon,
    ArrowUpIcon
} from '@heroicons/react/24/outline';

const FeatureGate = ({ 
    feature, 
    children, 
    fallback = null, 
    showUpgrade = true,
    className = "",
    upgradeMessage = null 
}) => {
    const { hasFeature, getUpgradeSuggestions, subscription, SUBSCRIPTION_TIERS, loading } = useSubscription();
    const { currentAgency, loading: crmLoading } = useCRM();

    // Show skeleton while loading subscription data, CRM data, or if subscription hasn't been properly loaded
    // Show skeleton until both CRM and subscription contexts have finished loading
    // This prevents showing "Feature Not Available" before we know the actual subscription state
    const isLoading = loading || crmLoading || !subscription;
    
    if (isLoading) {
        // Return a skeleton that matches the CollaborationHub layout
        return (
            <div className={`h-[calc(100vh-140px)] flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -mt-6 mb-0 animate-pulse ${className}`}>
                {/* Main Content Skeleton */}
                <div className="flex gap-0 flex-1 min-h-0 bg-background rounded-t-xl overflow-hidden border-x border-t border-border border-b-0 shadow-lg">
                    {/* Channels Sidebar Skeleton */}
                    <div className="w-80 bg-card border-r border-border flex flex-col flex-shrink-0 pl-4 sm:pl-6 lg:pl-8 pt-6">
                        <div className="pr-4 pb-4 border-b border-border">
                            <div className="h-7 bg-muted rounded w-32 mb-3"></div>
                            <div className="h-10 bg-muted rounded w-full mb-3"></div>
                            <div className="flex items-center space-x-4">
                                <div className="h-4 bg-muted rounded w-8"></div>
                                <div className="h-4 bg-muted rounded w-8"></div>
                                <div className="h-4 bg-muted rounded w-8"></div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto pr-2 pb-2 space-y-2">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="h-16 bg-muted rounded-xl mx-2"></div>
                            ))}
                        </div>
                    </div>
                    {/* Messages Area Skeleton */}
                    <div className="flex-1 flex flex-col min-w-0 pr-4 sm:pr-6 lg:pr-8">
                        <div className="p-4 border-b border-border bg-card flex-shrink-0">
                            <div className="h-8 bg-muted rounded w-48 mb-2"></div>
                            <div className="h-4 bg-muted rounded w-32"></div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <div className="h-4 bg-muted rounded w-24"></div>
                                    <div className="h-20 bg-muted rounded-lg w-3/4"></div>
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-border bg-card flex-shrink-0 rounded-b-xl">
                            <div className="h-11 bg-muted rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (hasFeature(feature)) {
        return <>{children}</>;
    }

    if (fallback) {
        return <>{fallback}</>;
    }

    if (!showUpgrade) {
        return null;
    }

    const upgradeSuggestions = getUpgradeSuggestions();
    const currentTier = subscription?.tier || 'FREE';

    const defaultUpgradeMessage = upgradeMessage || `This feature is not available in your ${subscription?.tier || 'Free'} plan.`;

    return (
        <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 ${className}`}>
            <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <LockClosedIcon className="w-6 h-6 text-blue-600" />
                    </div>
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                        Feature Not Available
                    </h3>
                    <p className="text-muted-foreground mb-4">
                        {defaultUpgradeMessage}
                    </p>
                    
                    {upgradeSuggestions.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="font-medium text-foreground">Upgrade to unlock:</h4>
                            {upgradeSuggestions.map((suggestion, index) => (
                                <div key={index} className="bg-card rounded-lg p-4 border border-border">
                                    <div className="flex items-center justify-between mb-2">
                                        <h5 className="font-medium text-foreground">{suggestion.tier} Plan</h5>
                                        <span className="text-sm text-muted-foreground">
                                            ${SUBSCRIPTION_TIERS[suggestion.tier]?.price}/month
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">{suggestion.reason}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {suggestion.features.map((feature, featureIndex) => (
                                            <span 
                                                key={featureIndex}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                            >
                                                <SparklesIcon className="w-3 h-3 mr-1" />
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                    <button className="mt-3 w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
                                        <ArrowUpIcon className="w-4 h-4 mr-2" />
                                        Upgrade to {suggestion.tier}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Usage limit component
export const UsageLimit = ({ action, children, fallback = null }) => {
    const { canPerformAction, getUsageWarnings, subscription, usage } = useSubscription();

    if (canPerformAction(action)) {
        return <>{children}</>;
    }

    if (fallback) {
        return <>{fallback}</>;
    }

    const warnings = getUsageWarnings();
    const relevantWarning = warnings.find(w => 
        (action === 'createClient' && w.message.includes('clients')) ||
        (action === 'createAccount' && w.message.includes('accounts')) ||
        (action === 'addUser' && w.message.includes('users'))
    );

    return (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h3 className="font-medium text-yellow-800 mb-1">Usage Limit Reached</h3>
                    <p className="text-sm text-yellow-700 mb-3">
                        {relevantWarning?.message || `You've reached your ${subscription?.tier || 'Free'} plan limit.`}
                    </p>
                    <p className="text-sm text-yellow-600">
                        {relevantWarning?.action || 'Upgrade your plan to continue adding more items.'}
                    </p>
                </div>
            </div>
        </div>
    );
};

// Feature badge component
export const FeatureBadge = ({ feature, className = "" }) => {
    const { hasFeature, subscription } = useSubscription();

    if (hasFeature(feature)) {
        return null; // Don't show badge if feature is available
    }

    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ${className}`}>
            <LockClosedIcon className="w-3 h-3 mr-1" />
            {subscription?.tier || 'Free'} Plan
        </span>
    );
};

export default FeatureGate;
